'use client'

import { useMemo } from 'react'
import Image from 'next/image'
import { useTheme } from '@/app/[locale]/ThemeProvider'
import {
  generateResponsiveSizes,
  DeviceCapabilityDetector,
  generateOptimizedImageUrl,
  isSupabaseStorageUrl
} from '@/lib/image-optimization'
import { getBlurDataURL } from '@/lib/blur-data-url'
import OptimizedImage from '@/components/OptimizedImage'
import SpaceIconWhite from '@/public/SpaceIcon3White.svg'
import SpaceIconAbyss from '@/public/SpaceIcon3Abyss.svg'

interface OptimizedProductImageProps {
  src: string | null | undefined
  alt: string
  priority?: boolean
  className?: string
  sizes?: string
  width?: number
  height?: number
  fill?: boolean
  quality?: number
  placeholder?: 'blur' | 'empty'
  onLoad?: () => void
  onError?: () => void
  // Product-specific props
  showSpaceIcon?: boolean
  productId?: string
  isAboveFold?: boolean
  watermarkSize?: 'small' | 'medium' | 'large'
}

export default function OptimizedProductImage({
  src,
  alt,
  priority = false,
  className = '',
  sizes,
  width,
  height,
  fill = false,
  quality,
  placeholder = 'blur',
  onLoad,
  onError,
  showSpaceIcon = true,
  isAboveFold = false,
  watermarkSize = 'medium'
}: OptimizedProductImageProps) {
  const { theme } = useTheme()

  // Determine if this should be priority based on above-fold position
  const shouldPrioritize = priority || isAboveFold

  // Generate responsive sizes if not provided
  const responsiveSizes = useMemo(() => {
    if (sizes) return sizes

    // Default product image sizes
    return generateResponsiveSizes(
      undefined, // Use default config
      'large' // Product images are typically large
    )
  }, [sizes])

  // Container style for consistent layout
  const containerStyle: React.CSSProperties = fill
    ? { position: 'relative', width: '100%', height: '100%' }
    : { width, height }

  // Determine watermark size and position
  const getWatermarkStyles = () => {
    switch (watermarkSize) {
      case 'small':
        return {
          size: 'w-6 h-6',
          position: 'top-2 right-2',
          iconSize: 24
        }
      case 'large':
        return {
          size: 'w-16 h-16',
          position: 'top-6 right-6',
          iconSize: 64
        }
      default: // medium
        return {
          size: 'w-12 h-12',
          position: 'top-4 right-4',
          iconSize: 48
        }
    }
  }

  const watermarkStyles = getWatermarkStyles()

  return (
    <div className="relative w-full h-full" style={containerStyle}>
      <OptimizedImage
        src={src}
        alt={alt}
        width={fill ? undefined : width}
        height={fill ? undefined : height}
        fill={fill}
        className={className}
        priority={shouldPrioritize}
        sizes={responsiveSizes}
        quality={quality}
        placeholder={placeholder}
        blurDataURL={getBlurDataURL(placeholder, width, height)}
        onLoad={onLoad}
        onError={onError}
        retryAttempts={3}
        retryDelay={1000}
        fallbackToOriginal={true}
      />
      {showSpaceIcon && (
        <div className={`absolute ${watermarkStyles.position} ${watermarkStyles.size}`}>
          <Image
            src={theme === 'dark' ? SpaceIconWhite : SpaceIconAbyss}
            alt=""
            width={watermarkStyles.iconSize}
            height={watermarkStyles.iconSize}
            className="w-full h-full opacity-80"
            priority={shouldPrioritize}
            unoptimized
          />
        </div>
      )}
    </div>
  )
}

/**
 * Preload critical product images for better LCP
 */
export async function preloadProductImages(
  imageUrls: string[],
  options: {
    priority?: boolean
    width?: number
    height?: number
  } = {}
): Promise<void> {
  const { priority = true, width = 800, height = 800 } = options

  const detector = DeviceCapabilityDetector.getInstance()
  await detector.detectCapabilities()

  const preloadPromises = imageUrls
    .filter(url => url && isSupabaseStorageUrl(url))
    .slice(0, priority ? 3 : 6) // Limit preloading to avoid overwhelming
    .map(async (url) => {
      try {
        const optimalFormat = detector.getOptimalFormat()
        const optimalQuality = detector.getOptimalQuality(priority)

        const optimizedUrl = generateOptimizedImageUrl(url, {
          width,
          height,
          quality: optimalQuality,
          format: optimalFormat
        })

        // Create link element for preloading
        const link = document.createElement('link')
        link.rel = 'preload'
        link.as = 'image'
        link.href = optimizedUrl

        if (priority) {
          link.fetchPriority = 'high'
        }

        document.head.appendChild(link)

        // Also preload via Image constructor for immediate caching
        return new Promise<void>((resolve, reject) => {
          const img = new window.Image()
          img.onload = () => resolve()
          img.onerror = reject
          img.src = optimizedUrl
        })
      } catch (error) {
        console.warn(`Failed to preload product image: ${url}`, error)
      }
    })

  await Promise.allSettled(preloadPromises)
}

/**
 * Generate product-specific responsive sizes
 */
export function generateProductImageSizes(): string {
  return generateResponsiveSizes(undefined, 'large')
}